/* global module, require */
const THREE = require('three'),
	crel = require('./createElement'),
	empty = require('./emptyElement'),
	unit = require('./unit');

const gDefaultAngle = 90,
	gDefaultLength = 4000,
	metre = unit.m(1);

function getGridSize(x, z) {
	var size = (x > z ? x : z) + metre * 2;
	var div = Math.floor(size / metre);
	const mod = size % metre;

	if (mod) div++;
	return div * metre;
}

function PlannerMeasurements(parent, data) {
	this.name = 'Dimensions';
	this.data = data || [{ length: gDefaultLength }];
	this.parent = parent;
	this.planner = parent.planner;
	this.called = false;

	this.container = crel({ className: 'planner-choose-dimensions' });
	this.addButton = crel({
		_tag: 'button',
		innerText: '+',
		className: 'add-section',
		onclick: this.addSection.bind(this),
	});

	this.addAt = this.addAt.bind(this);
}

PlannerMeasurements.prototype.complete = function() {
	return this.called;
};

PlannerMeasurements.prototype.activate = function() {
	this.called = true;

	empty(this.parent.content);
	this.parent.content.appendChild(this.container);
	this.rebuildDom();
};

PlannerMeasurements.prototype.rebuildDom = function() {
	const me = this;
	empty(me.container);

	crel({
		_parent: me.container,
		className: 'current-style',
		innerText: this.parent.planner.style.name.replace('\n', ' '),
	});

	crel({
		_parent: me.container,
		_tag: 'h2',
		innerText: 'Please input your measurements',
	});

	function onchange(e) {
		const { field, index } = e.target.dataset;
		const max = parseInt(e.target.max, 10);
		var value = e.target.valueAsNumber;

		if (max && value > max) {
			value = max;
			e.target.value = max;
		}

		me.data[index][field] = value;
		me.changed();
	}

	var wrapper = crel({ _parent: me.container, className: 'inputs' });

	me.data.forEach((m, i) => {
		var row = crel({ _parent: wrapper, className: 'row' });

		if (i) {
			var trem = crel({ _parent: row, className: 'label' });
			crel({
				_parent: trem,
				innerText: this.planner.call('getSectionName', i),
			});

			crel({
				_tag: 'button',
				_parent: trem,
				innerText: 'Remove',
				onclick: () => {
					me.data.splice(i, 1);
					me.rebuildDom();
				},
			});

			var angle = crel({
				_tag: 'label',
				_parent: row,
				className: 'angle',
				innerText: 'Angle (°):',
			});
			crel({
				_tag: 'input',
				_parent: angle,
				type: 'number',
				value: m.angle,
				step: 1,
				max: 360,
				onchange,
				dataset: {
					index: i,
					field: 'angle',
				},
			});
		} else {
			crel({
				_parent: row,
				className: 'label',
				innerText: this.planner.call('getSectionName', 0),
			});
		}

		var length = crel({
			_tag: 'label',
			_parent: row,
			className: 'length',
			innerText: 'Length (mm):',
		});
		crel({
			_tag: 'input',
			_parent: length,
			type: 'number',
			value: m.length,
			step: 1,
			max: 100000,
			onchange,
			dataset: {
				index: i,
				field: 'length',
			},
		});
	});

	me.container.appendChild(me.addButton);
	me.changed();
};

PlannerMeasurements.prototype.addSection = function() {
	this.data.push({ angle: gDefaultAngle, length: gDefaultLength });
	this.changed();

	this.rebuildDom();
};

PlannerMeasurements.prototype.addAt = function(destination) {
	const { heading, position } = this.getLastPosition();
	const between = new THREE.Vector3().copy(destination).sub(position);
	const turn = Math.atan2(between.x, between.z) - heading - Math.PI / 2;

	var angle = Math.round(THREE.Math.radToDeg(turn));
	while (angle < 0) angle += 360;

	const length = Math.round(unit.mmReverse(between.length()));

	this.data.push({ angle, length });
	this.changed();

	this.rebuildDom();
};

PlannerMeasurements.prototype.changed = function() {
	const position = new THREE.Vector3();
	const direction = new THREE.Vector3(1, 0, 0);
	const axis = new THREE.Vector3(0, 1, 0);

	// clear contents
	this.planner.style.disable();
	this.planner.style.addAt(position);

	this.data.forEach(m => {
		if (m.angle) direction.applyAxisAngle(axis, THREE.Math.degToRad(m.angle));

		const offset = new THREE.Vector3()
			.copy(direction)
			.multiplyScalar(unit.mm(m.length));

		position.add(offset);
		this.planner.style.addAt(position);
	});

	this.planner.style.update();
	this.updateGrid();
	this.planner.render();
};

PlannerMeasurements.prototype.updateGrid = function() {
	const bounds = this.planner.style.bounds();
	const xsize = bounds.max.x - bounds.min.x;
	const zsize = bounds.max.z - bounds.min.z;
	const size = getGridSize(xsize, zsize);

	if (this.gridHelper) {
		this.planner.scene.remove(this.gridHelper);
	}

	this.gridHelper = new THREE.GridHelper(size, size / metre);
	this.gridHelper.position
		.copy(bounds.min)
		.add(bounds.max)
		.divideScalar(2);
	this.planner.scene.add(this.gridHelper);
};

PlannerMeasurements.prototype.getLastPosition = function() {
	var heading = 0;
	const axis = new THREE.Vector3(0, 1, 0),
		direction = new THREE.Vector3(1, 0, 0),
		position = new THREE.Vector3();

	this.data.forEach(m => {
		if (m.angle) {
			const change = THREE.Math.degToRad(m.angle);
			direction.applyAxisAngle(axis, change);
			heading += change;
		}

		const offset = new THREE.Vector3()
			.copy(direction)
			.multiplyScalar(unit.mm(m.length));

		position.add(offset);
	});

	return { heading, position };
};

module.exports = PlannerMeasurements;
